import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { Field, FieldArray } from "@progress/kendo-react-form";
import FormTextField from "../../../components/formFields/FormTextField";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import { FaTimes } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getAllActiveB2BServices } from "../../b2bservice/service/b2bservice.services";
import IconComponent from "../../../components/common/IconComponent";
import FormIncrementalSearch from "../../../components/formFields/FormIncrementalSearch";
import { customizeHotelSearch, getHotelByID } from "../../hotel/services/hotel.services";
import "../b2bmodule.scss";

interface SpecialInclusionsProps {
    namePrefix: string;
    stayNightOptions: any[];
    hotelTotals: number[];
    onFormChange: (name: string, event: any) => void;
    destination: number | string;
}

const SpecialInclusionRow = ({
    i,
    namePrefix,
    stayNightOptions,
    b2bServiceOptions,
    onRemove,
    onFormChange,
    hotelId
}: any) => {
    const dispatch = useAppDispatch();
    const { SearchedHotelList } = useAppSelector((state) => state.hotel);
    const [extraHotelOptions, setExtraHotelOptions] = React.useState<any[]>([]);

    const handleHotelSearch = React.useCallback((search: string) => {
        if (!search || search.length < 3) return;
        dispatch(customizeHotelSearch({ HotelName: search }));
    }, [dispatch]);

    const hotelOptions = React.useMemo(() =>
        (SearchedHotelList || []).map((h: any) => ({ label: h.HotelName, value: h.ID })),
        [SearchedHotelList]);

    const allHotelOptions = React.useMemo(() => {
        const combined = [...hotelOptions];
        extraHotelOptions.forEach(extra => {
            if (!combined.find(opt => opt.value === extra.value)) {
                combined.push(extra);
            }
        });
        return combined;
    }, [hotelOptions, extraHotelOptions]);

    // Hydration logic
    React.useEffect(() => {
        if (hotelId && !allHotelOptions.some(opt => opt.value === hotelId)) {
            dispatch(getHotelByID(hotelId)).unwrap()
                .then((data: any) => {
                    if (data && data.HotelName) {
                        setExtraHotelOptions([{ label: data.HotelName, value: data.ID }]);
                    }
                })
                .catch(err => console.error("Hydration failed in SpecialInclusionRow:", err));
        }
    }, [hotelId, allHotelOptions, dispatch]);

    return (
        <div className="row g-2 align-items-end mb-3 pb-3 border-bottom border-light">
            <div className="col-12 col-md-2">
                <Field
                    name={`${namePrefix}[${i}].ServiceID`}
                    label="Service"
                    placeholder="Select Service"
                    options={b2bServiceOptions}
                    component={FormSelectionField}
                    onChange={(e: any) => {
                        const val = e.value;
                        onFormChange(`${namePrefix}[${i}].ServiceID`, { value: val });
                        const opt = b2bServiceOptions.find((o: any) => o.value === val);
                        if (opt) onFormChange(`${namePrefix}[${i}].ServiceName`, { value: opt.label });
                    }}
                />
            </div>
            <div className="col-12 col-md-3">
                <Field
                    name={`${namePrefix}[${i}].HotelID`}
                    label="Hotel"
                    placeholder="Search hotel..."
                    component={FormIncrementalSearch}
                    fetchIncrementalData={handleHotelSearch}
                    options={allHotelOptions}
                    onChange={(val: any) => {
                        onFormChange(`${namePrefix}[${i}].HotelID`, { value: val?.value });
                        onFormChange(`${namePrefix}[${i}].HotelName`, { value: val?.label });
                    }}
                />
            </div>
            <div className="col-12 col-md-2">
                <Field
                    name={`${namePrefix}[${i}].Night`}
                    label="Night"
                    placeholder="Select night..."
                    component={FormSelectionField}
                    options={stayNightOptions}
                    onChange={(e: any) => {
                        const label = e.value;
                        const nightNumMatch = label.match(/(\d+)/);
                        const nightNum = nightNumMatch ? parseInt(nightNumMatch[0]) : 0;
                        const datePart = label.split('(')[1]?.replace(')', '') || "";

                        onFormChange(`${namePrefix}[${i}].Night`, { value: label });
                        onFormChange(`${namePrefix}[${i}].DayNo`, { value: nightNum });
                        if (datePart) {
                            onFormChange(`${namePrefix}[${i}].Date`, { value: datePart });
                        }
                    }}
                />
            </div>
            <div className="col-6 col-md-1">
                <Field
                    name={`${namePrefix}[${i}].TotalPrice`}
                    label="Price"
                    placeholder="Price..."
                    component={FormNumericTextField}
                />
            </div>
            <div className="col-10 col-md-3">
                <Field
                    name={`${namePrefix}[${i}].Comment`}
                    label="Comments"
                    placeholder="Any comments..."
                    component={FormTextField}
                />
            </div>
            <div className="col-2 col-md-1 text-center pb-2">
                <div
                    className="b2b-remove-icon shadow-sm"
                    onClick={() => onRemove({ index: i })}
                    title="Remove Inclusion"
                >
                    <IconComponent icon={FaTimes} style={{ fontSize: "11px" }} />
                </div>
            </div>
        </div>
    );
};

const SpecialInclusionsList = (props: any) => {
    // Referencing BasicDetailsSection.tsx established pattern: use onPush and onRemove
    const { value, onPush, onRemove, namePrefix, stayNightOptions, hotelTotals, activeB2BServices, onFormChange, destination } = props;
    const inclusions = value || [];

    const b2bServiceOptions = (activeB2BServices || []).map((s: any) => ({ label: s.ServiceName, value: s.ID }));

    const totalHotelCost = hotelTotals.reduce((acc: number, current: number) => acc + current, 0);
    const totalInclusionsCost = inclusions.reduce((acc: number, item: any) => acc + (Number(item?.TotalPrice) || 0), 0);
    const grandTotal = totalHotelCost + totalInclusionsCost;

    return (
        <div className="px-0">
            {inclusions.map((item: any, i: number) => (
                <SpecialInclusionRow
                    key={i}
                    item={item}
                    i={i}
                    namePrefix={namePrefix}
                    stayNightOptions={stayNightOptions}
                    b2bServiceOptions={b2bServiceOptions}
                    onRemove={onRemove}
                    onFormChange={onFormChange}
                    destination={destination}
                    hotelId={item.HotelID}
                />
            ))}

            <div className="mt-3 text-start d-flex justify-content-between align-items-center">
                <Button
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    className="fw-bold px-3 py-1"
                    onClick={() => onPush({ value: { ServiceID: 0, HotelID: 0, Night: "", DayNo: 0, Date: null, TotalPrice: 0, Comment: "" } })}
                    style={{ fontSize: "13px", borderRadius: "6px" }}
                >
                    + Add Service
                </Button>

                <div className="accommodation-total-bar py-2 px-4 shadow-sm" style={{ borderRadius: "6px", background: "#fef9c3", border: "1px solid #fde047" }}>
                    <span className="label" style={{ fontSize: "14px", color: "#854d0e", fontWeight: 700 }}>Accommodations Total:</span>
                    <span className="amount fw-bold ms-2" style={{ fontSize: "18px", color: "#854d0e" }}>
                        INR {grandTotal.toLocaleString()}
                    </span>
                </div>
            </div>
        </div>
    );
};

const SpecialInclusions: React.FC<SpecialInclusionsProps> = ({
    namePrefix = "packageOptions[0].specialInclusions", // Fallback for safety
    stayNightOptions,
    hotelTotals = [],
    onFormChange,
    destination
}) => {
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        dispatch(getAllActiveB2BServices());
    }, [dispatch]);

    const activeB2BServices = useAppSelector((state: any) => state.b2bservice.B2BServiceList || []);

    return (
        <div className="b2b-quotation-module mt-1 pt-3 border-top border-light">
            <Typography.h6 className="fw-bold text-dark mb-0" style={{ fontSize: "15px" }}>Any special inclusions in hotels</Typography.h6>
            <Typography.p className="section-subtitle mb-3" style={{ fontSize: "12.5px" }}>
                Add any extra services for hotels e.g. special dinner, honeymoon cake etc.
            </Typography.p>

            <FieldArray
                name={namePrefix}
                component={SpecialInclusionsList}
                namePrefix={namePrefix}
                stayNightOptions={stayNightOptions}
                hotelTotals={hotelTotals}
                activeB2BServices={activeB2BServices}
                onFormChange={onFormChange}
                destination={destination}
            />
        </div>
    );
};

export default SpecialInclusions;
